var ontologyFields = require('./ontologyFields');

function cloneJson(value) {
  if (typeof value === 'undefined') return undefined;
  return JSON.parse(JSON.stringify(value));
}

var LEGACY_COLUMN_UNIT_ALIASES = {
  '%': { kind: 'unitless', defaultUnit: '%' },
  A: { kind: 'current', defaultUnit: 'A' },
  Hz: { kind: 'frequency', defaultUnit: 'Hz' },
  V: { kind: 'potential', defaultUnit: 'V' },
  cfh: { kind: 'gas_flow', defaultUnit: 'ft3/h' },
  cf: { kind: 'volume', defaultUnit: 'ft3' },
  cfm: { kind: 'air_flow', defaultUnit: 'ft3/m' },
  degC: { kind: 'temperature', defaultUnit: 'degC' },
  degF: { kind: 'temperature', defaultUnit: 'degF' },
  gal: { kind: 'volume', defaultUnit: 'gal' },
  gpm: { kind: 'liquid_flow', defaultUnit: 'gal/min' },
  h: { kind: 'time', defaultUnit: 'h' },
  inH2O: { kind: 'air_pressure', defaultUnit: 'inH2O' },
  kW: { kind: 'power', defaultUnit: 'kW' },
  kWh: { kind: 'energy', defaultUnit: 'kWh' },
  min: { kind: 'time', defaultUnit: 'min' },
  psi: { kind: 'pressure', defaultUnit: 'psi' }
};

var STRING_COLUMN_PROPS = ['chartType', 'definedBy', 'defaultUnit', 'kind', 'name', 'type'];
var STRING_DATASET_PROPS = ['definedBy', 'id', 'name', 'srcPath', 'type', 'visible', 'width'];
var DEPRECATED_COLUMN_PROPS = ['alias', 'color', 'icon', 'visible'];

function trimString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function hasMeaningfulValue(value) {
  if (typeof value === 'undefined' || value === null) return false;
  if (typeof value === 'string') return value.trim() !== '';
  return true;
}

function normalizeDefaultUnitToken(value) {
  var trimmed = trimString(value);
  if (!trimmed) return '';
  if (trimmed === 'bool') return '';
  return LEGACY_COLUMN_UNIT_ALIASES[trimmed] && LEGACY_COLUMN_UNIT_ALIASES[trimmed].defaultUnit
    ? LEGACY_COLUMN_UNIT_ALIASES[trimmed].defaultUnit
    : trimmed;
}

function getColumnUnitMetadata(column) {
  if (!column || typeof column !== 'object') return null;
  var defaultUnit = normalizeDefaultUnitToken(column.defaultUnit);
  if (defaultUnit && LEGACY_COLUMN_UNIT_ALIASES[defaultUnit]) {
    return LEGACY_COLUMN_UNIT_ALIASES[defaultUnit];
  }
  var legacyUnit = trimString(column.unit);
  if (legacyUnit && LEGACY_COLUMN_UNIT_ALIASES[legacyUnit]) {
    return LEGACY_COLUMN_UNIT_ALIASES[legacyUnit];
  }
  return null;
}

function getColumnName(column) {
  if (typeof column === 'string') return trimString(column);
  if (column && typeof column === 'object') {
    return trimString(column.name || column.columnName);
  }
  return '';
}

function getDatasetName(dataset) {
  if (dataset && typeof dataset === 'object') {
    return trimString(dataset.name || dataset.datasetName);
  }
  return '';
}

function normalizeNameKey(value) {
  return trimString(value).toLowerCase();
}

function normalizeColumnDefinition(column) {
  if (typeof column !== 'string' && (!column || typeof column !== 'object')) {
    return null;
  }

  var normalized = typeof column === 'string'
    ? { name: trimString(column) }
    : cloneJson(column);

  var columnName = getColumnName(normalized);
  if (!columnName) return null;

  normalized.name = columnName;

  STRING_COLUMN_PROPS.forEach(function (key) {
    if (!Object.prototype.hasOwnProperty.call(normalized, key)) return;
    var trimmed = trimString(normalized[key]);
    if (trimmed) {
      normalized[key] = trimmed;
    } else if (key !== 'name') {
      delete normalized[key];
    }
  });

  var metadata = getColumnUnitMetadata(normalized);
  if (!normalized.kind && metadata && metadata.kind) {
    normalized.kind = metadata.kind;
  }

  var defaultUnit = normalizeDefaultUnitToken(normalized.defaultUnit || normalized.unit);
  if (!defaultUnit && metadata && metadata.defaultUnit) {
    defaultUnit = metadata.defaultUnit;
  }
  if (defaultUnit) {
    normalized.defaultUnit = defaultUnit;
  } else {
    delete normalized.defaultUnit;
  }

  if (!trimString(normalized.kind)) {
    delete normalized.kind;
  }

  DEPRECATED_COLUMN_PROPS.forEach(function (key) {
    delete normalized[key];
  });
  delete normalized.unit;

  return normalized;
}

function mergeColumnDefinitions(baseColumn, incomingColumn) {
  var base = normalizeColumnDefinition(baseColumn) || {};
  var incoming = normalizeColumnDefinition(incomingColumn) || {};
  var merged = {};
  var keys = Array.from(new Set(Object.keys(base).concat(Object.keys(incoming))));

  keys.forEach(function (key) {
    if (key === 'unit') return;
    if (key === 'name') {
      merged.name = incoming.name || base.name || '';
      return;
    }
    if (hasMeaningfulValue(incoming[key])) {
      merged[key] = cloneJson(incoming[key]);
      return;
    }
    if (hasMeaningfulValue(base[key])) {
      merged[key] = cloneJson(base[key]);
    }
  });

  return normalizeColumnDefinition(merged);
}

function normalizeColumnDefinitions(columns) {
  var orderedNames = [];
  var byName = {};

  (Array.isArray(columns) ? columns : []).forEach(function (column) {
    var normalized = normalizeColumnDefinition(column);
    var columnName = getColumnName(normalized);
    if (!columnName) return;
    if (!byName[columnName]) {
      byName[columnName] = normalized;
      orderedNames.push(columnName);
      return;
    }
    byName[columnName] = mergeColumnDefinitions(byName[columnName], normalized);
  });

  return orderedNames.map(function (columnName) {
    return byName[columnName];
  });
}

function mergeColumnArrays(baseColumns, incomingColumns) {
  var merged = {};
  var orderedNames = [];

  normalizeColumnDefinitions(baseColumns).forEach(function (column) {
    var columnName = getColumnName(column);
    if (!columnName) return;
    merged[columnName] = column;
    orderedNames.push(columnName);
  });

  normalizeColumnDefinitions(incomingColumns).forEach(function (column) {
    var columnName = getColumnName(column);
    if (!columnName) return;
    if (!merged[columnName]) {
      merged[columnName] = column;
      orderedNames.push(columnName);
      return;
    }
    merged[columnName] = mergeColumnDefinitions(merged[columnName], column);
  });

  return orderedNames.map(function (columnName) {
    return merged[columnName];
  });
}

function normalizeDatasetDefinition(dataset) {
  if (!dataset || typeof dataset !== 'object') return null;
  var normalized = cloneJson(dataset);
  var datasetName = getDatasetName(normalized);
  if (!datasetName) return null;

  normalized.name = datasetName;
  STRING_DATASET_PROPS.forEach(function (key) {
    if (!Object.prototype.hasOwnProperty.call(normalized, key)) return;
    var trimmed = trimString(normalized[key]);
    if (trimmed) {
      normalized[key] = trimmed;
    } else if (key !== 'name') {
      delete normalized[key];
    }
  });

  normalized.columns = normalizeColumnDefinitions(normalized.columns);
  return normalized;
}

function mergeDatasetDefinitions(baseDataset, incomingDataset) {
  var base = normalizeDatasetDefinition(baseDataset) || {};
  var incoming = normalizeDatasetDefinition(incomingDataset) || {};
  var merged = {};
  var keys = Array.from(new Set(Object.keys(base).concat(Object.keys(incoming))));

  keys.forEach(function (key) {
    if (key === 'columns') return;
    if (key === 'name') {
      merged.name = incoming.name || base.name || '';
      return;
    }
    if (hasMeaningfulValue(incoming[key])) {
      merged[key] = cloneJson(incoming[key]);
      return;
    }
    if (hasMeaningfulValue(base[key])) {
      merged[key] = cloneJson(base[key]);
    }
  });

  merged.columns = mergeColumnArrays(base.columns, incoming.columns);
  return normalizeDatasetDefinition(merged);
}

function normalizeDatasetDefinitions(datasets) {
  var orderedNames = [];
  var byName = {};

  (Array.isArray(datasets) ? datasets : []).forEach(function (dataset) {
    var normalized = normalizeDatasetDefinition(dataset);
    var datasetName = normalized && normalized.name;
    if (!datasetName) return;
    if (!byName[datasetName]) {
      byName[datasetName] = normalized;
      orderedNames.push(datasetName);
      return;
    }
    byName[datasetName] = mergeDatasetDefinitions(byName[datasetName], normalized);
  });

  return orderedNames.map(function (datasetName) {
    return byName[datasetName];
  });
}

function validateDatasetDefinitions(datasets) {
  var errors = [];
  var datasetNames = {};

  (Array.isArray(datasets) ? datasets : []).forEach(function (dataset) {
    var datasetName = getDatasetName(dataset);
    var datasetNameKey = normalizeNameKey(datasetName);
    if (!datasetNameKey) return;

    if (datasetNames[datasetNameKey]) {
      errors.push('Dataset names must be unique. ' + datasetName + ' is repeated.');
    } else {
      datasetNames[datasetNameKey] = true;
    }

    var columnNames = {};
    (Array.isArray(dataset.columns) ? dataset.columns : []).forEach(function (column) {
      var columnName = getColumnName(column);
      var columnNameKey = normalizeNameKey(columnName);
      if (!columnNameKey) return;
      if (columnNames[columnNameKey]) {
        errors.push('Column names on ' + datasetName + ' dataset must be unique. ' + columnName + ' is repeated.');
      } else {
        columnNames[columnNameKey] = true;
      }
    });
  });

  return errors;
}

function nextDatasetId(datasets) {
  var ids = (Array.isArray(datasets) ? datasets : [])
    .map(function (dataset) {
      return Number(dataset && dataset.id);
    })
    .filter(Number.isFinite);
  return ids.length > 0 ? Math.max.apply(null, ids) + 1 : 1;
}

function mergeDatasetArrays(baseDatasets, incomingDatasets) {
  var merged = {};
  var orderedNames = [];

  normalizeDatasetDefinitions(baseDatasets).forEach(function (dataset) {
    if (!dataset || !dataset.name) return;
    merged[dataset.name] = dataset;
    orderedNames.push(dataset.name);
  });

  normalizeDatasetDefinitions(incomingDatasets).forEach(function (dataset) {
    if (!dataset || !dataset.name) return;
    if (!merged[dataset.name]) {
      merged[dataset.name] = dataset;
      orderedNames.push(dataset.name);
      return;
    }
    merged[dataset.name] = mergeDatasetDefinitions(merged[dataset.name], dataset);
  });

  return orderedNames.map(function (datasetName) {
    return merged[datasetName];
  });
}

module.exports = {
  LEGACY_COLUMN_UNIT_ALIASES: cloneJson(LEGACY_COLUMN_UNIT_ALIASES),
  getColumnName: getColumnName,
  getDatasetName: getDatasetName,
  getUnitsForKind: ontologyFields.getUnitsForKind,
  listKnownColumnKinds: ontologyFields.listKnownFieldKinds,
  mergeColumnArrays: mergeColumnArrays,
  mergeColumnDefinitions: mergeColumnDefinitions,
  mergeDatasetArrays: mergeDatasetArrays,
  mergeDatasetDefinitions: mergeDatasetDefinitions,
  nextDatasetId: nextDatasetId,
  normalizeColumnDefinition: normalizeColumnDefinition,
  normalizeColumnDefinitions: normalizeColumnDefinitions,
  normalizeDatasetDefinition: normalizeDatasetDefinition,
  normalizeDatasetDefinitions: normalizeDatasetDefinitions,
  validateDatasetDefinitions: validateDatasetDefinitions
};
